import React, { useEffect, useState } from 'react';
import { makeRequest } from '@/makeRequest';
import { MY_ENDPOINTS } from '@raashidh/shared';
import { TrendingUp, TrendingDown, Minus, Search, Filter } from 'lucide-react';

type Stock = {
  company: string;
  code: string;
  type: string;
  price: number;
  movement: string;
};

type FilterType = 'All' | 'Equity' | 'Debt';
type SortBy = 'code' | 'price' | 'movement' | 'company';

export default function MvStocks() {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [filteredStocks, setFilteredStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('All');
  const [sortBy, setSortBy] = useState<SortBy>('code');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Remove duplicates by code (keeping first occurrence)
  const removeDuplicates = (data: Stock[]): Stock[] => {
    const uniqueMap = new Map();
    data.forEach(item => {
      if (!uniqueMap.has(item.code)) {
        uniqueMap.set(item.code, item);
      }
    });
    return Array.from(uniqueMap.values());
  };

  const fetchStocks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await makeRequest<Stock[]>(
        MY_ENDPOINTS.SCRAPER_ENDPOINTS.GET_MV_STOCKS?.path,
        {
          method: 'GET',
          auth: true, // Set to true if authentication is required
        }
      );

      // Remove duplicates and set data
      const uniqueStocks = removeDuplicates(response);
      setStocks(uniqueStocks);
      setFilteredStocks(uniqueStocks);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching stocks:', err);
      setError('Failed to load stock data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
    
    // Optional: Auto-refresh every 5 minutes
    const interval = setInterval(fetchStocks, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Filter and sort stocks
  useEffect(() => {
    let result = [...stocks];

    // Filter by type
    if (filterType !== 'All') {
      result = result.filter(stock => stock.type === filterType);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        stock =>
          stock.company.toLowerCase().includes(term) ||
          stock.code.toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aValue: any = a[sortBy];
      let bValue: any = b[sortBy];

      if (sortBy === 'price') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else if (sortBy === 'movement') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredStocks(result);
  }, [stocks, filterType, searchTerm, sortBy, sortOrder]);

  const getMovementIcon = (movement: string) => {
    const value = parseFloat(movement);
    if (value > 0) return <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />;
    if (value < 0) return <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />;
    return <Minus className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
  };

  const getMovementColor = (movement: string) => {
    const value = parseFloat(movement);
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column: SortBy) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const stats = {
    total: filteredStocks.length,
    equity: filteredStocks.filter(s => s.type === 'Equity').length,
    debt: filteredStocks.filter(s => s.type === 'Debt').length,
    gainers: filteredStocks.filter(s => parseFloat(s.movement) > 0).length,
    losers: filteredStocks.filter(s => parseFloat(s.movement) < 0).length,
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[400px] bg-white dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading stock data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20 bg-white dark:bg-gray-900 min-h-[400px]">
        <div className="text-5xl mb-4">📉</div>
        <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error Loading Stocks</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
        <button
          onClick={fetchStocks}
          className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Maldives Stock Market
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Real-time stock prices from the Maldives Stock Exchange
          </p>
          {lastUpdated && (
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* Stats Cards */}
        <div className="flex flex-wrap gap-2 mb-6">
          <div className="bg-blue-50 dark:bg-blue-950/30 p-2 rounded-lg border border-blue-100 dark:border-blue-900">
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Stocks</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.total}</p>
          </div>
          <div className="bg-green-50 dark:bg-green-950/30 p-2 rounded-lg border border-green-100 dark:border-green-900">
            <p className="text-sm text-gray-600 dark:text-gray-400">Equity</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.equity}</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-950/30 p-2 rounded-lg border border-purple-100 dark:border-purple-900">
            <p className="text-sm text-gray-600 dark:text-gray-400">Debt</p>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.debt}</p>
          </div>
          <div className="bg-green-50 dark:bg-green-950/30 p-2 rounded-lg border border-green-100 dark:border-green-900">
            <p className="text-sm text-gray-600 dark:text-gray-400">Gainers</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.gainers}</p>
          </div>
          <div className="bg-red-50 dark:bg-red-950/30 p-2 rounded-lg border border-red-100 dark:border-red-900">
            <p className="text-sm text-gray-600 dark:text-gray-400">Losers</p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.losers}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by company name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2">
            {(['All', 'Equity', 'Debt'] as FilterType[]).map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                  filterType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Filter className="w-4 h-4" />
                {type}
              </button>
            ))}
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchStocks}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            Refresh
          </button>
        </div>

        {/* Stocks Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th
                  onClick={() => handleSort('code')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Code {getSortIcon('code')}
                </th>
               
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th
                  onClick={() => handleSort('price')}
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Price (MVR) {getSortIcon('price')}
                </th>
                <th
                  onClick={() => handleSort('movement')}
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Movement {getSortIcon('movement')}
                </th>
                 <th
                  onClick={() => handleSort('company')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Company {getSortIcon('company')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
              {filteredStocks.map((stock, index) => (
                <tr
                  key={`${stock.code}-${index}`}
                  className="h-12 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                >
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {stock.code}
                  </td>
                  
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        stock.type === 'Equity'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      }`}
                    >
                      {stock.type}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 dark:text-white">
                    {stock.price.toFixed(2)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {getMovementIcon(stock.movement)}
                      <span className={`text-sm font-medium ${getMovementColor(stock.movement)}`}>
                        {stock.movement}
                      </span>
                    </div>
                  </td>
                  <td className=" px-4 py-4 text-xs text-wrap text-gray-700 dark:text-gray-300">
                    {stock.company}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredStocks.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No stocks found matching your criteria</p>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-500">
          Data provided for informational purposes only. Stock prices are subject to market risks.
        </div>
      </div>
    </div>
  );
}